import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mail, Copy, Check, ExternalLink, X, Mic, BookOpen, Heart, AlertTriangle } from 'lucide-react';

interface CreatorWelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  isFirstLaunch?: boolean;
  onContactClick?: () => void;
  onOpenLegal?: () => void;
}

export const CreatorWelcomeModal: React.FC<CreatorWelcomeModalProps> = ({
  isOpen,
  onClose,
  isFirstLaunch = false,
  onOpenLegal,
}) => {
  const [copied, setCopied] = useState(false);

  const contactEmail = 'baron.yoann35@gmail.com';

  const copyEmail = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(contactEmail).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => {
      // Fallback
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[100000] flex items-center justify-center p-3 sm:p-5 bg-black/85 sm:backdrop-blur-md overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 10 }}
          transition={{ type: 'spring', stiffness: 350, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-xl bg-[#FBF5E2] border-2 border-amber-700/60 shadow-[0_10px_50px_rgba(0,0,0,0.4)] rounded-2xl sm:rounded-3xl p-5 sm:p-7 text-stone-900 flex flex-col gap-4 sm:gap-5 my-auto max-h-[92vh] overflow-y-auto select-text"
        >
          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="absolute top-3.5 right-3.5 sm:top-4 sm:right-4 p-2 rounded-full bg-stone-300/60 hover:bg-stone-300 text-stone-700 hover:text-stone-950 transition-all cursor-pointer z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header Badge & Title */}
          <div className="flex flex-col items-center text-center gap-1.5 pt-1">
            <div className="flex items-center gap-2">
              <span className="text-2xl sm:text-3xl text-amber-700 font-black">
                ⵣ
              </span>
              <span className="px-3 py-0.5 rounded-full bg-amber-200/80 border border-amber-600/40 text-amber-950 text-[11px] sm:text-xs font-cinzel font-bold uppercase tracking-widest">
                Message du Créateur
              </span>
              <span className="text-2xl sm:text-3xl text-amber-700 font-black">
                ⵣ
              </span>
            </div>

            <h2 className="text-xl sm:text-2xl font-black font-cinzel text-amber-950 tracking-wide mt-1">
              Azul fell-awen !
            </h2>
            <p className="text-xs sm:text-sm text-amber-900 font-semibold">
              Bienvenue dans Quizz Kabyle : Aventures & Défis
            </p>
          </div>

          {/* Core Message Cards */}
          <div className="flex flex-col gap-3 sm:gap-3.5 text-xs sm:text-sm leading-relaxed text-stone-800">
            
            {/* Identity & Motivation Card */}
            <div className="p-3.5 sm:p-4 rounded-xl bg-[#f4ebd0] border border-amber-600/30 flex gap-3 items-start shadow-xs">
              <div className="p-2 rounded-lg bg-amber-200/80 border border-amber-500/40 text-amber-900 shrink-0 mt-0.5">
                <Heart className="w-5 h-5 fill-amber-500/40 text-amber-700" />
              </div>
              <div className="space-y-1">
                <p className="font-bold text-amber-950 text-sm font-cinzel">
                  Une passerelle entre la Bretagne et la Kabylie
                </p>
                <p className="text-stone-800">
                  Je m'appelle <strong>Yoann Baron</strong>, je suis un Breton passionné par la culture et la langue kabyles (<span className="text-amber-900 font-medium italic">Tamazight</span>). J'ai créé ce jeu pour apprendre moi-même la langue encore davantage et partager cet apprentissage avec vous tous.
                </p>
              </div>
            </div>

            {/* Collaborative Voice & Dictionary Appeal with Pronunciation Warning */}
            <div className="p-3.5 sm:p-4 rounded-xl bg-gradient-to-br from-amber-100/90 via-[#f4ebd0] to-orange-100/80 border-2 border-amber-600/50 flex gap-3 items-start shadow-sm">
              <div className="p-2 rounded-lg bg-amber-300/70 border border-amber-500/50 text-amber-950 shrink-0 mt-0.5">
                <Mic className="w-5 h-5 text-amber-900 animate-pulse" />
              </div>
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-2 py-0.5 rounded bg-amber-600 text-amber-50 text-[10px] font-black uppercase tracking-wider flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3 text-amber-200" />
                    Attention
                  </span>
                  <p className="font-bold text-amber-950 text-sm font-cinzel">
                    Prononciation des mots & Enregistrement des voix
                  </p>
                </div>
                <p className="text-stone-800">
                  La prononciation actuelle des mots (générée par synthèse vocale) <strong>n'est pas tout à fait correcte</strong>.
                </p>
                <p className="text-stone-800">
                  C'est pourquoi <strong>je recherche activement des personnes pour enregistrer des voix kabyles authentiques</strong> ! Tout est déjà configuré et prêt dans l'application pour enregistrer facilement et à distance depuis chez vous afin d'enrichir le dictionnaire.
                </p>
              </div>
            </div>

            {/* Volunteer & Free spirit */}
            <div className="p-3.5 sm:p-4 rounded-xl bg-[#f4ebd0] border border-amber-600/30 flex gap-3 items-start shadow-xs">
              <div className="p-2 rounded-lg bg-amber-200/80 border border-amber-500/40 text-amber-900 shrink-0 mt-0.5">
                <BookOpen className="w-5 h-5 text-amber-800" />
              </div>
              <div className="space-y-1">
                <p className="font-bold text-amber-950 text-sm font-cinzel">
                  Un projet 100% bénévole et ouvert à tous
                </p>
                <p className="text-stone-800">
                  C'est une démarche bénévole et passionnée : l'application est destinée dans un premier temps à être <strong>accessible gratuitement à toutes et à tous, dans son intégralité</strong>.
                </p>
              </div>
            </div>

          </div>

          {/* Contact Section - Direct Email Display */}
          <div className="rounded-xl border border-amber-600/40 bg-[#f0e4c3] p-3.5 sm:p-4 flex flex-col gap-2.5 shadow-xs">
            <div>
              <span className="text-xs sm:text-sm font-bold text-amber-950 flex items-center gap-1.5 font-cinzel">
                <Mail className="w-4 h-4 text-amber-800" />
                Envie de participer au projet ou d'enregistrer des voix ?
              </span>
              <p className="text-[11px] sm:text-xs text-stone-700 mt-0.5">
                Contactez-moi directement par e-mail :
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 bg-white/90 rounded-lg border border-amber-500/40 shadow-xs">
              <div className="flex items-center gap-2 text-xs text-stone-900 truncate">
                <span className="text-amber-900 font-bold">Email :</span>
                <span className="font-mono select-all text-amber-950 font-semibold">{contactEmail}</span>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={copyEmail}
                  className="px-2.5 py-1 rounded bg-stone-200 hover:bg-stone-300 text-stone-800 text-[11px] font-medium flex items-center gap-1 border border-stone-400 transition-colors cursor-pointer"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="text-emerald-700 font-bold">Copié !</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5 text-amber-800" />
                      <span>Copier</span>
                    </>
                  )}
                </button>

                <a
                  href={`mailto:${contactEmail}?subject=${encodeURIComponent("Participation au projet Quizz Kabyle - Enregistrement de voix & Dictionnaire")}&body=${encodeURIComponent("Azul Yoann,\n\nJe souhaite participer au projet collaboratif Quizz Kabyle (enregistrer des voix / proposer du vocabulaire / échanger) :\n\n")}`}
                  className="px-2.5 py-1 rounded bg-amber-700 hover:bg-amber-800 text-white text-[11px] font-bold flex items-center gap-1 shadow-sm transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Écrire un e-mail</span>
                </a>
              </div>
            </div>

            <p className="text-[11px] text-amber-900/90 italic text-center sm:text-left">
              Toute contribution est la bienvenue : locuteurs natifs, passionnés, enseignants ou apprenants !
            </p>
          </div>

          {/* Footer Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t border-amber-900/20">
            <div className="flex flex-col text-[11px] text-stone-600 text-center sm:text-left">
              <span>Vous retrouverez ce message et le contact à tout moment.</span>
              <div className="flex items-center gap-1.5 mt-0.5 justify-center sm:justify-start">
                <span>© 2026 Yoann Baron</span>
                {onOpenLegal && (
                  <>
                    <span>•</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenLegal();
                      }}
                      className="text-amber-900 hover:text-amber-950 font-medium underline underline-offset-2 transition-colors cursor-pointer"
                    >
                      Mentions Légales & Sources
                    </button>
                  </>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-amber-700 via-amber-600 to-amber-700 hover:from-amber-600 hover:to-amber-600 text-amber-50 font-cinzel font-black text-sm sm:text-base rounded-xl shadow-[0_0_20px_rgba(180,83,9,0.3)] border border-amber-500 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2 shrink-0"
            >
              {isFirstLaunch ? (
                <>
                  <span>Continuer (Tutoriel)</span>
                  <span className="text-amber-100 font-black">▶</span>
                </>
              ) : (
                <>
                  <span>J'ai compris</span>
                  <span className="text-amber-200 font-black">✓</span>
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
